import logger from "../utils/logger";
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import ApiService from '../services/api';
import { getStoredAuthToken } from '../utils/authTokens';

const Navigation = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { language, changeLanguage, t, isRTL } = useLanguage();

  useEffect(() => {
    // Check if user is logged in
    const token = getStoredAuthToken({ preferAdmin: false });
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      setIsLoggedIn(true);
      setUser(JSON.parse(userData));
    }
  }, []);

  const handleDashboardNavigation = () => {
    const token = getStoredAuthToken({ preferAdmin: false });
    if (token) {
      navigate('/dashboard');
    } else {
      navigate('/login');
    }
  };

  const handleLogout = async () => {
    try {
      await ApiService.logout();
    } catch (error) {
      logger.error('Logout error:', error);
    } finally {
      ApiService.removeToken();
      localStorage.removeItem('user');
      setIsLoggedIn(false);
      setUser(null);
      setShowUserMenu(false);
      navigate('/');
    }
  };

  const getUserInitials = (user) => {
    if (!user) return 'U';
    const firstInitial = user.first_name ? user.first_name.charAt(0).toUpperCase() : '';
    const lastInitial = user.last_name ? user.last_name.charAt(0).toUpperCase() : '';
    return firstInitial + lastInitial || user.username?.charAt(0).toUpperCase() || 'U';
  };

  const handleLanguageChange = (newLanguage) => {
    changeLanguage(newLanguage);
    setShowLanguageMenu(false);
  };

  const getActiveClass = (path) => {
    if (path === '/' && location.pathname === '/') return 'text-blue-700 font-medium';
    if (path !== '/' && location.pathname.startsWith(path)) return 'text-blue-700 font-medium';
    return 'text-gray-600 hover:text-blue-700';
  };

  return (
    <nav className="fixed w-full bg-white shadow-sm z-50 top-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="flex items-center">
                <img src="/navebarlogo.png" alt="Jelani AI" className="w-130 h-12 object-contain" />
                </Link>
            </div>
          </div>
          
          {/* Desktop Menu */}
          <div className={`hidden md:flex items-center ${isRTL ? 'space-x space-x-14' : 'space-x-8'}`}>
            <Link to="/" className={`${getActiveClass('/')} transition ml-8`}>{t('home')}</Link>
            <Link to="/features" className={`${getActiveClass('/features')} transition`}>{t('features')}</Link>
            <Link to="/pricing" className={`${getActiveClass('/pricing')} transition`}>{t('pricing')}</Link>
            <Link to="/resources" className={`${getActiveClass('/resources')} transition`}>{t('resources')}</Link>
            <Link to="/about" className={`${getActiveClass('/about')} transition`}>{t('about')}</Link>
            <Link to="/blog" className={`${getActiveClass('/blog')} transition`}>{t('blog')}</Link>
          <button
                  onClick={handleDashboardNavigation}
                  className="px-4 py-2 text-gray-700 font-medium hover:text-blue-700 transition border border-gray-300 rounded-md hover:border-blue-300"
                >
                 
                  {t('dashboard')}
                </button>
          </div>
          
          {/* Desktop Auth Section */}
          <div className={`hidden md:flex items-center ${isRTL ? 'space-x-reverse space-x-4' : 'space-x-8'}`}>
            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => handleLanguageChange(language === "en" ? "ar" : "en")}
                className="flex items-center gap-2 px-3 py-1 rounded-full text-sm border border-gray-200 hover:bg-gray-50 transition"
              >
                <span>{language === "en" ? "AR" : "EN"}</span>
                <i className="fas fa-globe text-gray-500 shrink-0"></i>
              </button>
              
              {showLanguageMenu && (
                <div 
                  className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-32 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50`}
                  onMouseLeave={() => setShowLanguageMenu(false)}
                >
                  <button
                    onClick={() => handleLanguageChange('en')}
                    className={`block w-full ${isRTL ? 'text-right' : 'text-left'} px-4 py-2 text-sm hover:bg-gray-100 ${language === 'en' ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}
                  >
                    <i className={`fas fa-globe ${isRTL ? 'ml-2' : 'mr-2'}`}></i>
                    EN
                  </button>
                  <button
                    onClick={() => handleLanguageChange('ar')}
                    className={`block w-full ${isRTL ? 'text-right' : 'text-left'} px-4 py-2 text-sm hover:bg-gray-100 ${language === 'ar' ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}
                  >
                    <i className={`fas fa-globe ${isRTL ? 'ml-2' : 'mr-2'}`}></i>
                    AR
                  </button>
                </div>
              )}
            </div>
            
            {isLoggedIn ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  onMouseEnter={() => setShowUserMenu(true)}
                  className="flex items-center gap-2 p-2 rounded-full hover:bg-gray-100 transition"
                >
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium text-sm">
                    {getUserInitials(user)}
                  </div>
                  <span className="text-gray-700 font-medium">{user?.first_name || user?.username}</span>
                  <i className="fas fa-chevron-down text-gray-500 text-xs"></i>
                </button>
                
                {showUserMenu && (
                  <div 
                    className={`absolute ${isRTL ? 'left-0' : 'right-0'} z-50 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1`}
                    dir={isRTL ? 'rtl' : 'ltr'}
                    onMouseLeave={() => setShowUserMenu(false)}
                  >
                    <Link
                      to="/dashboard"
                      className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 transition"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <i className="fas fa-tachometer-alt shrink-0"></i>
                      {t('dashboard')}
                    </Link>
                    
                    <Link
                      to="/settings"
                      className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 transition"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <i className="fas fa-cog shrink-0"></i>
                      {t('settings')}
                    </Link>
                    <hr className="my-1" />
                    <button
                      onClick={handleLogout}
                      className={`flex w-full items-center gap-2 ${isRTL ? 'text-right' : 'text-left'} px-4 py-2 text-red-600 hover:bg-gray-100 transition`}
                    >
                      <i className="fas fa-sign-out-alt shrink-0"></i>
                      {t('logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                
                <Link to="/login" className="px-4 py-2 text-blue-700 font-medium hover:text-blue-900 transition">
                  {t('login')}
                </Link>
                <Link to="/signup" className="px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition shadow-sm">
                  {t('getStarted')}
                </Link>
              </>
            )}
          </div>
          
          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-blue-700 focus:outline-none"
            >
              <i className={`fas ${showMobileMenu ? 'fa-times' : 'fa-bars'}`}></i>
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {showMobileMenu && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <Link
                to="/"
                className="block px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                onClick={() => setShowMobileMenu(false)}
              >
                {t('home')}
              </Link>
              <Link
                to="/features"
                className="block px-3 py-2 text-blue-700 font-medium hover:bg-gray-100 rounded-md"
                onClick={() => setShowMobileMenu(false)}
              >
                {t('features')}
              </Link>
              <Link
                to="/pricing"
                className="block px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                onClick={() => setShowMobileMenu(false)}
              >
                {t('pricing')}
              </Link>
              <Link
                to="/resources"
                className="block px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                onClick={() => setShowMobileMenu(false)}
              >
                {t('resources')}
              </Link>
              <Link
                to="/about"
                className="block px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                onClick={() => setShowMobileMenu(false)}
              >
                {t('about')}
              </Link>
              <Link
                to="/blog"
                className="block px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                onClick={() => setShowMobileMenu(false)}
              >
                {t('blog')}
              </Link>
              
              {isLoggedIn ? (
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center px-3 py-2">
                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium text-sm">
                      {getUserInitials(user)}
                    </div>
                    <span className="ml-3 text-gray-700 font-medium">{user?.first_name || user?.username}</span>
                  </div>
                  <Link
                    to="/dashboard"
                    className="block px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <i className="fas fa-tachometer-alt mr-2"></i>
                    {t('dashboard')}
                  </Link>
                  <Link
                    to="/profile"
                    className="block px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <i className="fas fa-user mr-2"></i>
                    {t('profile')}
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-3 py-2 text-red-600 hover:bg-gray-100 rounded-md"
                  >
                    <i className="fas fa-sign-out-alt mr-2"></i>
                    {t('logout')}
                  </button>
                </div>
              ) : (
                <div className="border-t border-gray-200 pt-4 space-y-1">
                  <button
                    onClick={() => { handleDashboardNavigation(); setShowMobileMenu(false); }}
                    className="block w-full text-left px-3 py-2 text-gray-700 font-medium hover:bg-gray-100 rounded-md"
                  >
                    <i className="fas fa-tachometer-alt mr-2"></i>
                    {t('dashboard')}
                  </button>
                  <Link
                    to="/login"
                    className="block px-3 py-2 text-blue-700 font-medium hover:bg-gray-100 rounded-md"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    {t('login')}
                  </Link>
                  <Link
                    to="/signup"
                    className="block px-3 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition mx-3"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    {t('getStarted')}
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
